import { useState, useEffect, useRef, useCallback } from "react";

export const GAME_WIDTH = 100;
export const GAME_HEIGHT = 150;
export const PADDLE_WIDTH = 30;
export const PADDLE_HEIGHT = 4;
export const BALL_RADIUS = 2.5;

const AI_HIT_TOLERANCE_X = 5;
const AI_HIT_TOLERANCE_Y = 3;
const PLAYER_HIT_TOLERANCE_X = 45;
const PLAYER_HIT_TOLERANCE_Y = 30;

export type Difficulty = "easy" | "medium" | "hard";

export type GameState = {
  ball: {
    x: number;
    y: number;
    z: number;
    dx: number;
    dy: number;
    dz: number;
    spinX: number;
    type: "normal" | "topspin" | "backspin";
  };
  playerX: number;
  playerY: number;
  aiX: number;
  aiY: number;
  score: { player: number; ai: number };
  sets: { player: number; ai: number };
  isPlaying: boolean;
  isServe: boolean;
  lastHitter: "player" | "ai" | null;
  server: "player" | "ai";
  matchWinner: "player" | "ai" | null;
  tossPhase: "idle" | "tossing";
  playerVelX: number;
  playerVelY: number;
  aiVelX: number;
};

export function usePingPongEngine(
  targetSets: number = 3,
  difficulty: Difficulty = "medium",
) {
  const [gameState, setGameState] = useState<GameState>({
    ball: {
      x: GAME_WIDTH / 2,
      y: GAME_HEIGHT / 2,
      z: 0,
      dx: 0,
      dy: 0,
      dz: 0,
      spinX: 0,
      type: "normal",
    },
    playerX: GAME_WIDTH / 2,
    playerY: GAME_HEIGHT - 5,
    aiX: GAME_WIDTH / 2,
    aiY: 5,
    score: { player: 0, ai: 0 },
    sets: { player: 0, ai: 0 },
    isPlaying: false,
    isServe: false,
    lastHitter: null,
    server: "player",
    matchWinner: null,
    tossPhase: "idle",
    playerVelX: 0,
    playerVelY: 0,
    aiVelX: 0,
  });

  const stateRef = useRef(gameState);
  const audioCtxRef = useRef(null as AudioContext | null);
  const requestRef = useRef(null as number | null);

  const lastPaddlePos = useRef({ x: GAME_WIDTH / 2, y: GAME_HEIGHT - 5 });
  const paddleVel = useRef({ x: 0, y: 0 });
  const aiServeTimer = useRef(0);

  const isDragging = useRef(false);
  const lastPointerPos = useRef({ x: 0, y: 0 });

  const initAudio = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
    }
    if (audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
  };

  const playHitSound = (type: "paddle" | "wall" | "score") => {
    if (!audioCtxRef.current) return;
    const ctx = audioCtxRef.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    if (type === "paddle") {
      osc.type = "sine";
      osc.frequency.setValueAtTime(600, ctx.currentTime);
      gain.gain.setValueAtTime(1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.1);
    } else if (type === "wall") {
      osc.type = "square";
      osc.frequency.setValueAtTime(300, ctx.currentTime);
      gain.gain.setValueAtTime(0.5, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.1);
    } else {
      osc.type = "triangle";
      osc.frequency.setValueAtTime(150, ctx.currentTime);
      gain.gain.setValueAtTime(1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    }
  };

  const resetMatch = () => {
    stateRef.current = {
      ball: {
        x: GAME_WIDTH / 2,
        y: GAME_HEIGHT / 2,
        z: 0,
        dx: 0,
        dy: 0,
        dz: 0,
        spinX: 0,
        type: "normal",
      },
      playerX: GAME_WIDTH / 2,
      playerY: GAME_HEIGHT - 5,
      aiX: GAME_WIDTH / 2,
      aiY: 5,
      score: { player: 0, ai: 0 },
      sets: { player: 0, ai: 0 },
      isPlaying: false,
      isServe: false,
      lastHitter: null,
      server: "player",
      matchWinner: null,
      tossPhase: "idle",
      playerVelX: 0,
      playerVelY: 0,
      aiVelX: 0,
    };
    setGameState(stateRef.current);
  };

  const handleScore = (state: GameState, winner: "player" | "ai") => {
    state.score[winner] += 1;
    state.isPlaying = false;
    state.tossPhase = "idle";

    let pScore = state.score.player;
    let aScore = state.score.ai;

    const isSetOver =
      (pScore >= 11 || aScore >= 11) && Math.abs(pScore - aScore) >= 2;

    if (isSetOver) {
      const setWinner = pScore > aScore ? "player" : "ai";
      state.sets[setWinner] += 1;
      state.score = { player: 0, ai: 0 };

      if (state.sets.player === targetSets || state.sets.ai === targetSets) {
        state.matchWinner = state.sets.player === targetSets ? "player" : "ai";
      }
    }

    pScore = state.score.player;
    aScore = state.score.ai;
    const totalScore = pScore + aScore;

    if (pScore >= 10 && aScore >= 10) {
      state.server = totalScore % 2 === 0 ? "player" : "ai";
    } else {
      state.server = Math.floor(totalScore / 2) % 2 === 0 ? "player" : "ai";
    }

    aiServeTimer.current = 60;
    playHitSound("score");
  };

  const gameLoop = useCallback(() => {
    const state = { ...stateRef.current };
    if (state.matchWinner) return;

    let { ball, aiX, aiY, playerX, playerY, isServe, lastHitter, server } =
      state;

    paddleVel.current.x = playerX - lastPaddlePos.current.x;
    paddleVel.current.y = playerY - lastPaddlePos.current.y;
    lastPaddlePos.current.x = playerX;
    lastPaddlePos.current.y = playerY;

    // Định hình thông số phản xạ dựa vào độ khó
    const aiConfig = {
      easy: { speedBase: 0.2, speedMax: 0.5, wobble: 40 },
      medium: { speedBase: 0.6, speedMax: 1.5, wobble: 20 },
      hard: { speedBase: 1.0, speedMax: 2.2, wobble: 10 },
    }[difficulty];

    if (!state.isPlaying) {
      if (server === "player") {
        if (state.tossPhase === "idle") {
          ball.x = playerX;
          ball.y = playerY - PADDLE_HEIGHT - BALL_RADIUS - 1;
          ball.z = 5;
          ball.dx = 0;
          ball.dy = 0;
          ball.dz = 0;
          ball.spinX = 0;
          ball.type = "normal";
        } else if (state.tossPhase === "tossing") {
          ball.z += ball.dz;
          ball.dz -= 0.15;

          const dist = Math.sqrt(
            Math.pow(ball.x - playerX, 2) + Math.pow(ball.y - playerY, 2),
          );

          if (ball.dz < 0 && paddleVel.current.y < -0.5 && dist < 40) {
            state.isPlaying = true;
            state.isServe = true;
            state.lastHitter = "player";
            state.tossPhase = "idle";

            ball.dy = paddleVel.current.y * 0.8;
            ball.dx = paddleVel.current.x * 0.5;
            ball.spinX = paddleVel.current.x * 0.8;

            ball.dy = Math.max(-3.5, Math.min(-1.5, ball.dy));
            ball.dx = Math.max(-2.5, Math.min(2.5, ball.dx));
            ball.z = Math.max(0, ball.z);

            playHitSound("paddle");
          } else if (ball.z <= 0) {
            handleScore(state, "ai");
            state.tossPhase = "idle";
          }
        }
      } else {
        const serveAiSpeed = aiConfig.speedBase;
        if (aiX < GAME_WIDTH / 2 - 2) aiX += serveAiSpeed;
        else if (aiX > GAME_WIDTH / 2 + 2) aiX -= serveAiSpeed;
        aiY = 5;

        if (state.tossPhase === "idle") {
          ball.x = aiX;
          ball.y = aiY + PADDLE_HEIGHT + BALL_RADIUS + 1;
          ball.z = 5;
          ball.dx = 0;
          ball.dy = 0;
          ball.dz = 0;
          ball.spinX = 0;
          ball.type = "normal";

          if (aiServeTimer.current > 0) {
            aiServeTimer.current -= 1;
          } else {
            state.tossPhase = "tossing";
            ball.dz = 3.5;
          }
        } else if (state.tossPhase === "tossing") {
          ball.z += ball.dz;
          ball.dz -= 0.15;

          if (ball.dz < 0 && ball.z <= 12) {
            state.isPlaying = true;
            state.isServe = true;
            state.lastHitter = "ai";
            state.tossPhase = "idle";

            ball.dy =
              difficulty === "easy"
                ? 1.1 + Math.random() * 0.2
                : 1.4 + Math.random() * 0.2;

            let serveTargetX = GAME_WIDTH / 2;
            if (difficulty === "easy") {
              serveTargetX = GAME_WIDTH * (0.4 + Math.random() * 0.2);
              ball.spinX = 0;
            } else {
              serveTargetX =
                playerX < GAME_WIDTH / 2
                  ? GAME_WIDTH * 0.72
                  : GAME_WIDTH * 0.28;
              ball.spinX = playerX < GAME_WIDTH / 2 ? 0.7 : -0.7;
            }

            const stepsToBounce = Math.floor(
              (GAME_HEIGHT * 0.75 - aiY) / ball.dy,
            );
            let currentSpin = ball.spinX;
            let totalDriftX = 0;
            let accumulatedDx = 0;
            for (let i = 0; i < stepsToBounce; i++) {
              accumulatedDx += currentSpin * 0.025;
              totalDriftX += accumulatedDx;
              currentSpin *= 0.96;
            }

            ball.dx = (serveTargetX - ball.x - totalDriftX) / stepsToBounce;
            ball.z = Math.max(0, ball.z);
            playHitSound("paddle");
          } else if (ball.z <= 0) {
            handleScore(state, "player");
            state.tossPhase = "idle";
          }
        }
      }

      stateRef.current = {
        ...state,
        ball,
        aiX,
        aiY,
        playerVelX: paddleVel.current.x,
        playerVelY: paddleVel.current.y,
        aiVelX: 0,
      };
      setGameState(stateRef.current);
      requestRef.current = requestAnimationFrame(gameLoop);
      return;
    }

    if (state.isPlaying) {
      ball.dx += ball.spinX * 0.025;
      ball.spinX *= 0.96;
    }

    const prevY = ball.y;
    ball.x += ball.dx;
    ball.y += ball.dy;

    let targetX = ball.x;
    if (ball.dy < 0) {
      const timeToReach = Math.max(0.1, (ball.y - aiY) / Math.abs(ball.dy));

      let currentSpin = ball.spinX;
      let totalDriftX = 0;
      let accumulatedDx = 0;
      const steps = Math.floor(timeToReach);
      for (let i = 0; i < steps; i++) {
        accumulatedDx += currentSpin * 0.025;
        totalDriftX += accumulatedDx;
        currentSpin *= 0.96;
      }

      if (difficulty === "easy") {
        targetX = ball.x + ball.dx * timeToReach;
      } else {
        targetX = ball.x + ball.dx * timeToReach + totalDriftX;
      }

      const distanceFactor = Math.min(1, (GAME_HEIGHT - ball.y) / GAME_HEIGHT);
      targetX += Math.sin(ball.y * 0.08) * aiConfig.wobble * distanceFactor;
    } else {
      targetX = GAME_WIDTH / 2;
    }

    const distanceToTarget = targetX - aiX;
    const currentAiSpeed = Math.max(
      aiConfig.speedBase,
      Math.min(aiConfig.speedMax, Math.abs(distanceToTarget) * 0.25),
    );
    const oldAiX = aiX;

    if (aiX < targetX - 1) aiX += currentAiSpeed;
    else if (aiX > targetX + 1) aiX -= currentAiSpeed;
    aiX = Math.max(-GAME_WIDTH / 2, Math.min(GAME_WIDTH * 1.5, aiX));
    const aiVelX = aiX - oldAiX;

    let isOut = false;

    // ĐIỀU CHỈNH ĐỘ BỔNG QUỸ ĐẠO DỰA TRÊN KỸ NĂNG
    let MAX_Z = 12;
    if (ball.type === "topspin") {
      MAX_Z = 6.5;
    } else if (ball.type === "backspin") {
      MAX_Z = 22;
    }

    let p =
      ball.dy < 0 ? (GAME_HEIGHT - ball.y) / GAME_HEIGHT : ball.y / GAME_HEIGHT;
    p = Math.max(0, Math.min(1, p));
    let prevP =
      ball.dy < 0 ? (GAME_HEIGHT - prevY) / GAME_HEIGHT : prevY / GAME_HEIGHT;

    if (isServe) {
      ball.z = MAX_Z * Math.abs(Math.cos(p * Math.PI * 2));
      if ((prevP < 0.25 && p >= 0.25) || (prevP < 0.75 && p >= 0.75)) {
        if (ball.x < 0 || ball.x > GAME_WIDTH) {
          handleScore(state, lastHitter === "player" ? "ai" : "player");
          isOut = true;
        } else {
          ball.dx += ball.spinX * 0.35;
          ball.spinX *= 0.2;
          playHitSound("wall");
        }
      }
    } else {
      if (p <= 0.75) {
        const t = p / 0.75;
        ball.z = MAX_Z * (1 - Math.pow(2 * t - 1, 2));
      } else {
        const t = (p - 0.75) / 0.25;
        ball.z = MAX_Z * 0.6 * (1 - Math.pow(t - 1, 2));
      }
      if (prevP < 0.75 && p >= 0.75) {
        if (ball.x < 0 || ball.x > GAME_WIDTH) {
          handleScore(state, lastHitter === "player" ? "ai" : "player");
          isOut = true;
        } else {
          ball.dx += ball.spinX * 0.35;
          ball.spinX *= 0.2;
          playHitSound("wall");
        }
      }
    }

    if (isOut) {
      stateRef.current = {
        ...state,
        ball,
        aiX,
        playerX,
        playerY,
        playerVelX: paddleVel.current.x,
        playerVelY: paddleVel.current.y,
        aiVelX,
      };
      setGameState(stateRef.current);
      requestRef.current = requestAnimationFrame(gameLoop);
      return;
    }

    // --- MÁY ĐÁNH TRẢ PHẢN CÔNG THEO ĐỘ KHÓ CHIẾN THUẬT ---
    if (
      ball.dy < 0 &&
      ball.y - BALL_RADIUS <= aiY + PADDLE_HEIGHT / 2 + AI_HIT_TOLERANCE_Y &&
      ball.y + BALL_RADIUS >= aiY - PADDLE_HEIGHT / 2 - AI_HIT_TOLERANCE_Y &&
      ball.x >= aiX - PADDLE_WIDTH / 2 - AI_HIT_TOLERANCE_X &&
      ball.x <= aiX + PADDLE_WIDTH / 2 + AI_HIT_TOLERANCE_X
    ) {
      const bounceY = GAME_HEIGHT * 0.75;

      let safeTargetX = playerX;
      let maxVelocity = 1.2;

      if (difficulty === "easy") {
        ball.dy = 0.7 + Math.random() * 0.2;
        safeTargetX = GAME_WIDTH / 2;
        ball.spinX = 0;
        maxVelocity = 0.8;
      } else if (difficulty === "medium") {
        ball.dy = 0.9 + Math.random() * 0.2;
        safeTargetX = playerX;
        ball.spinX = 0;
        maxVelocity = 1.2;
      } else {
        ball.dy = 1.1 + Math.random() * 0.2;
        safeTargetX = playerX + (Math.random() > 0.5 ? 10 : -10);
        ball.spinX = playerX < GAME_WIDTH / 2 ? 0.3 : -0.3;
        maxVelocity = 1.5;
      }

      const stepsToBounce = (bounceY - aiY) / ball.dy;
      let currentSpin = ball.spinX;
      let totalDriftX = 0;
      let accumulatedDx = 0;
      const steps = Math.floor(stepsToBounce);
      for (let i = 0; i < steps; i++) {
        accumulatedDx += currentSpin * 0.025;
        totalDriftX += accumulatedDx;
        currentSpin *= 0.96;
      }

      let idealDx = (safeTargetX - ball.x - totalDriftX) / stepsToBounce;

      // CƠ CHẾ UNFORCED ERRORS: Tỉ lệ AI tự bóp cò đánh bóng ra ngoài (out)
      const errorChance =
        difficulty === "easy" ? 0.35 : difficulty === "medium" ? 0.2 : 0.08;
      if (Math.random() < errorChance) {
        idealDx = idealDx > 0 ? 5.0 : -5.0;
      }

      const minSafeDx =
        (GAME_WIDTH * 0.1 - ball.x - totalDriftX) / stepsToBounce;
      const maxSafeDx =
        (GAME_WIDTH * 0.9 - ball.x - totalDriftX) / stepsToBounce;

      if (Math.abs(idealDx) !== 5.0) {
        ball.dx = Math.max(minSafeDx, Math.min(maxSafeDx, idealDx));
      } else {
        ball.dx = idealDx;
      }

      ball.dx = Math.max(-maxVelocity, Math.min(maxVelocity, ball.dx));

      if (difficulty === "hard" && Math.random() > 0.5) {
        ball.type = "topspin";
      } else if (difficulty === "easy") {
        ball.type = "backspin";
      } else {
        ball.type = "normal";
      }

      state.isServe = false;
      state.lastHitter = "ai";
      playHitSound("paddle");
    }

    // --- NGƯỜI CHƠI ĐÁNH TRẢ BÓNG ---
    if (
      ball.dy > 0 &&
      ball.y + BALL_RADIUS >=
        playerY - PADDLE_HEIGHT / 2 - PLAYER_HIT_TOLERANCE_Y &&
      ball.y - BALL_RADIUS <=
        playerY + PADDLE_HEIGHT / 2 + PLAYER_HIT_TOLERANCE_Y &&
      ball.x >= playerX - PADDLE_WIDTH / 2 - PLAYER_HIT_TOLERANCE_X &&
      ball.x <= playerX + PADDLE_WIDTH / 2 + PLAYER_HIT_TOLERANCE_X
    ) {
      const hitPower = Math.abs(Math.min(0, paddleVel.current.y));

      // 1. PHÂN LOẠI KỸ NĂNG: Giật (Topspin) vs Cắt (Backspin)
      let basePower = 1.2;
      if (hitPower > 2.0) {
        basePower = 2.0;
        ball.dy = -(basePower + hitPower * 0.9);
        ball.dy = Math.max(-5.5, ball.dy);
        ball.type = "topspin";
      } else if (hitPower < 0.6) {
        basePower = 0.8;
        ball.dy = -(basePower + hitPower * 0.3);
        ball.dy = Math.max(-2.0, ball.dy);
        ball.type = "backspin";
      } else {
        basePower = 1.2;
        ball.dy = -(basePower + hitPower * 0.7);
        ball.dy = Math.max(-3.8, ball.dy);
        ball.type = "normal";
      }

      // 2. Tính toán hướng bóng ngang ban đầu dựa trên lực vung tay
      let rawDx = paddleVel.current.x * 0.25 + (ball.x - playerX) * 0.1;

      // 3. HỆ THỐNG AIM ASSIST (Hỗ trợ nắn góc an toàn)
      const targetBounceY = 35;
      const stepsToAiSide = Math.abs((ball.y - targetBounceY) / ball.dy);

      const minSafeDx = (15 - ball.x) / stepsToAiSide;
      const maxSafeDx = (GAME_WIDTH - 15 - ball.x) / stepsToAiSide;

      ball.dx = Math.max(minSafeDx, Math.min(maxSafeDx, rawDx));

      // 4. Giới hạn độ xoáy (spin)
      ball.spinX = paddleVel.current.x * 0.6;
      ball.spinX = Math.max(-1.5, Math.min(1.5, ball.spinX));

      state.isServe = false;
      state.lastHitter = "player";
      playHitSound("paddle");
    }

    if (ball.y < -30) {
      handleScore(state, "player");
    } else if (ball.y > GAME_HEIGHT + 60) {
      handleScore(state, "ai");
    }

    stateRef.current = {
      ...state,
      ball,
      aiX,
      playerX,
      playerY,
      playerVelX: paddleVel.current.x,
      playerVelY: paddleVel.current.y,
      aiVelX,
    };
    setGameState(stateRef.current);
    requestRef.current = requestAnimationFrame(gameLoop);
  }, [targetSets, difficulty]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    requestRef.current = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(requestRef.current!);
  }, [gameLoop]);

  const handlePointerMove = (e: React.PointerEvent) => {
    initAudio();
    const svg = e.currentTarget as SVGSVGElement;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;

    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(ctm.inverse());

    let newPlayerX = stateRef.current.playerX;
    let newPlayerY = stateRef.current.playerY;

    if (e.pointerType === "touch") {
      if (!isDragging.current) return;

      const deltaX_viewBox = svgP.x - lastPointerPos.current.x;
      const deltaY_viewBox = svgP.y - lastPointerPos.current.y;

      const percentY = Math.max(0, Math.min(1, newPlayerY / GAME_HEIGHT));
      const widthAtY = 160 + percentY * (260 - 160);

      const deltaGameX = deltaX_viewBox * (GAME_WIDTH / widthAtY);
      const deltaGameY = deltaY_viewBox * (GAME_HEIGHT / 220);

      newPlayerX += deltaGameX;
      newPlayerY += deltaGameY;

      lastPointerPos.current = { x: svgP.x, y: svgP.y };
    } else {
      const mouseScaleY = (svgP.y - 40) / 220;
      newPlayerY = mouseScaleY * GAME_HEIGHT;

      const percentY = Math.max(0, Math.min(1, newPlayerY / GAME_HEIGHT));
      const widthAtY = 160 + percentY * (260 - 160);
      newPlayerX = 50 + (100 * (svgP.x - 150)) / widthAtY;
    }

    newPlayerX = Math.max(-10, Math.min(GAME_WIDTH + 10, newPlayerX));
    newPlayerY = Math.max(
      GAME_HEIGHT / 2,
      Math.min(GAME_HEIGHT + 20, newPlayerY),
    );

    stateRef.current.playerX = newPlayerX;
    stateRef.current.playerY = newPlayerY;
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    const svg = e.currentTarget as SVGSVGElement;
    svg.setPointerCapture(e.pointerId);
    initAudio();

    const ctm = svg.getScreenCTM();
    if (!ctm) return;

    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(ctm.inverse());

    lastPointerPos.current = { x: svgP.x, y: svgP.y };
    isDragging.current = true;

    if (e.pointerType !== "touch") {
      handlePointerMove(e);
    }

    if (
      !stateRef.current.isPlaying &&
      stateRef.current.server === "player" &&
      stateRef.current.tossPhase === "idle" &&
      !stateRef.current.matchWinner
    ) {
      stateRef.current.tossPhase = "tossing";
      stateRef.current.ball.dz = 4.0;
      stateRef.current.ball.dx = 0;
      stateRef.current.ball.dy = 0;
      stateRef.current.ball.spinX = 0;
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    isDragging.current = false;
    try {
      (e.currentTarget as SVGSVGElement).releasePointerCapture(e.pointerId);
    } catch (err) {}
  };

  return {
    gameState,
    handlePointerMove,
    handlePointerDown,
    handlePointerUp,
    resetMatch,
  };
}
